import { REWARD_TIERS } from '@bulknutrients/bulk-shared-lib/constants';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import { Accordion } from '@components/Accordion';
import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { Icons } from '@components/Icons/Icons';
import { StandardLayout } from '@components/Layouts';
import { LoginModalUncontrolled } from '@components/LoginForm/LoginModal';
import {
  RewardsLink,
  TextLink,
} from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import Head from 'next/head';
import Image from 'next/image';
import type { ReactElement } from 'react';
import { twJoin } from 'tailwind-merge';
import hero from '../../public/images/rewards/hero image.webp';
import spending from '../../public/images/rewards/nicola-bulk-products.webp';

// Leaving this here for reference if we need to change icons
// import { renderToStaticMarkup } from 'react-dom/server';
// const reactSvgComponentToMarkupString = (Component) =>
//   `data:image/svg+xml,${encodeURIComponent(
//     renderToStaticMarkup(createElement(Component))
//   )}`;

function StandoutParagraph({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p
      className={mergeStyles(
        className ?? '',
        'mx-auto mb-[38px] mt-[9px] text-left text-base dark:text-white'
      )}
    >
      {children}
    </p>
  );
}

const FAQS = [
  {
    title: 'How do rewards points work?',
    content: (
      <>
        <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
          Points are accumulated by making purchases with Bulk Nutrients' online
          store. You can also complete various activities for bonus points. Your
          points are assigned to your email address used when ordering. In order
          to redeem points, you will need to ensure you make an account with us
          using that same address. Anyone with an account is automatically
          enrolled.
        </p>
        <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
          We often run promotions and competitions with Whey Cool Points, so be
          sure you're logged in watch this space!
        </p>
      </>
    ),
  },
  {
    title: "What's a tier?",
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        There are four tiers, stepped by how much you have spent with us. Each
        time you move up a tier, you will earn more Whey Cool Points per dollar
        spent, as well as access to various exclusive offers.
      </p>
    ),
  },
  {
    title: 'When does my tier reset?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        Your tier is calculated every 12 months from reaching a tier. If you
        haven't spent enough to requalify for your existing tier, you'll be
        dropped to a lower tier.
      </p>
    ),
  },
  {
    title: "What's the point of 'Whey Cool Points'?",
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        This is our way of showing our appreciation. You'll earn points for
        activities on our site, like social media follows and purchases. You can
        use them to earn discounts off purchases, so the more you collect the
        more you save.
      </p>
    ),
  },
  {
    title: 'How do I earn points?',
    content: (
      <div className="text-[0.9rem] mx-0 mb-[1.2rem] mt-[0.6rem] text-white">
        <p>It's super easy!</p>
        <ol style={{ color: 'theme(colors.grey.dark)' }}>
          <li>
            Ensure you're{' '}
            <LoginModalUncontrolled
              trigger={
                <Button
                  buttonStyle="text"
                  className="inline text-white hover:text-grey-mid"
                >
                  logged into your Bulk Nutrients account
                </Button>
              }
              title={<>Login to your account</>}
            />
          </li>
          <li>
            Navigate to the{' '}
            <RewardsLink className="text-white hover:text-grey-mid">
              Bulk Rewards
            </RewardsLink>{' '}
            page
          </li>
          <li>
            If you have enough points, you'll be able to click the reward and
            redeem it for points!
          </li>
        </ol>
      </div>
    ),
  },
  {
    title: 'Is there a limit to the number of points I can earn?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        No. Go ahead and earn as many as you can! Please Note: earning points
        via liking/following social media accounts can only occur once (i.e.
        unliking/unfollowing and re-liking/re-following will not earn you more
        points).
      </p>
    ),
  },
  {
    title: 'Do my points expire?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        Yes, unused points will expire after 24 months.
      </p>
    ),
  },
  {
    title: "What do the 'approved', 'pending' and 'cancelled' statuses mean?",
    content: (
      <>
        <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
          Approved: These points can be redeemed on rewards immediately
        </p>
        <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
          Pending: These points need to be verified before you can redeem them.
          This typically applies to purchases and referrals
        </p>
        <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
          Cancelled: These points will not be added to your account. For
          example, this will happen if you cancel a purchase (the points will
          change from pending to cancelled)
        </p>
      </>
    ),
  },
  {
    title: 'Why did my account balance go down?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        You, or someone you referred, cancelled or returned a purchase.
      </p>
    ),
  },
  {
    title: "I completed an activity but didn't earn points!",
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        It can sometimes take a few minutes for us to process your activity and
        provide your points.
      </p>
    ),
  },
  {
    title: 'Can I use my points during checkout?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        Not directly - please redeem your points for a voucher code which can
        then be applied during checkout.
      </p>
    ),
  },
  {
    title: 'How do I leave the program?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        If you no longer wish to earn points, please contact us and ask to be
        unenrolled. We'll unenroll you and you will lose any points you have
        accrued.
      </p>
    ),
  },
  {
    title: 'What happens if I leave and decide to join again?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        Just contact us and we'll re-enroll you. However, your point total will
        begin from zero.
      </p>
    ),
  },
  {
    title: 'Where can I report a problem or give feedback?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        Please{' '}
        <TextLink
          href="/contact-us"
          className="!text-white hover:!text-grey-mid"
        >
          contact us
        </TextLink>{' '}
        and we'll be glad to help!
      </p>
    ),
  },
  {
    title: 'How do I use my rewards?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        Your rewards points can be converted to a voucher code in your rewards
        dashboard, this code can then be applied during checkout. Rewards
        vouchers are only available in $5, $10, $25 and $50 amounts. Vouchers
        must be used in full in a single transaction.
      </p>
    ),
  },
  {
    title: 'Can I use more than one reward per transaction?',
    content: (
      <p className="mx-0 mb-[1.2rem] mt-[0.6rem] text-[0.9rem] text-white">
        Unfortunately only one rewards voucher can be used per transaction.
      </p>
    ),
  },
];

const REWARDS = [
  {
    icon: <Icons.Cart size="3rem" />,
    title: 'Make a purchase',
    points: `${REWARD_TIERS['WELCOME'].earn} points per $1`,
  },
  {
    icon: <Icons.User.Add size="3rem" />,
    title: 'Create an account',
    points: '250 points',
  },
  {
    icon: <Icons.Rewards.Birthday size="3rem" />,
    title: 'Happy birthday',
    points: '500 points',
  },
  {
    icon: <Icons.Social.Facebook.Standard size="3rem" />,
    title: 'Like us on Facebook',
    points: '50 points',
  },
  {
    icon: <Icons.Social.Instagram size="3rem" />,
    title: 'Follow us on Instagram',
    points: '50 points',
  },
  {
    icon: <Icons.Social.Tiktok size="3rem" />,
    title: 'Follow us on TikTok',
    points: '50 points',
  },
  {
    icon: <Icons.Rewards.WriteReview size="3rem" />,
    title: 'Write review via email',
    points: '100 points',
  },
];

const VOUCHERS = [
  {
    points: '500',
    value: '5',
  },
  {
    points: '1,000',
    value: '10',
  },
  {
    points: '2,500',
    value: '25',
  },
  {
    points: '5,000',
    value: '50',
  },
];

const RewardsWidget = () => (
  <div className="grid max-w-narrow-page grid-cols-2 items-center gap-4 text-black dark:text-white sm:grid-cols-3 md:grid-cols-4">
    {REWARDS.map((r) => (
      <div
        className="relative flex h-full flex-1 flex-col items-center justify-center gap-3 overflow-visible border-[0.5px] border-grey-mid px-2 pb-6 pt-4 text-center shadow-lg dark:bg-grey-darkest rounded-2xl"
        key={r.title}
      >
        <>{r.icon}</>
        <div className="md:min-h-14 min-h-10 text-base sm:text-lg">
          {r.title}
        </div>
        <div className="absolute -bottom-2 border-[0.5px] border-grey-mid bg-grey-light px-3 py-1 text-xs font-bold uppercase text-orange shadow-sm dark:bg-grey-dark md:text-base rounded-full">
          {r.points}
        </div>
      </div>
    ))}
  </div>
);

const VoucherWidget = () => (
  <div className="absolute left-0 right-0 mx-auto grid max-w-narrow-page grid-cols-2 gap-4 px-4 text-black dark:text-white sm:grid-cols-4">
    {VOUCHERS.map((r) => (
      <div
        className="relative flex h-full flex-1 flex-col items-center justify-center gap-3 overflow-visible border-[0.5px] border-grey-mid bg-white p-9 pb-[3.5rem] text-center uppercase shadow-lg dark:bg-grey-darkest rounded-2xl"
        key={r.points}
      >
        <div className="text-2xl font-bold sm:text-3xl">
          {r.points}
          <br />
          points
        </div>
        <div className="absolute -bottom-2 border-[0.5px] border-grey-mid bg-grey-light px-3 py-1 font-bold uppercase text-orange shadow-sm dark:bg-grey-dark rounded-full">
          ${r.value} voucher
        </div>
      </div>
    ))}
  </div>
);

const TiersWidget = () => {
  const tierBgColours = [
    'bg-black',
    'bg-[hsl(30,58%,54%)]',
    'bg-[hsl(0,0%,75%)]',
    'bg-[hsl(36,36%,52%)]',
  ];

  const tierTextColours = [
    'text-black',
    'text-[hsl(30,58%,54%)]',
    'text-[hsl(0,0%,40%)]',
    'text-[hsl(36,36%,52%)]',
  ];

  return (
    <div className="mb-[38px] grid grid-cols-2 gap-2 sm:grid-cols-4">
      {Object.keys(REWARD_TIERS).map((tier, idx) => (
        <div
          key={tier}
          className={twJoin([
            tierBgColours[idx],
            idx === 0 ? 'border-[0.5px]' : 'border-0', // or empty string
            'flex flex-1 flex-col items-center border-black p-2 text-white shadow-lg dark:border-white rounded-2xl',
          ])}
        >
          <div className="text-2xl font-bold uppercase drop-shadow-lg">
            {tier}
          </div>
          <HorizontalDivider className="m-2! from-white! via-white! to-white! drop-shadow-lg" />
          <div className="font-bold drop-shadow-lg">
            {REWARD_TIERS[tier].earn} points per $1
          </div>
          <div
            className={twJoin([
              tierTextColours[idx],
              'mt-4 w-full bg-white py-1 text-center font-bold uppercase shadow-lg rounded-full',
            ])}
          >
            {idx === 0 ? 'start here' : `spend $${REWARD_TIERS[tier].spend}`}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Rewards() {
  const { customer } = useCustomer();

  return (
    <>
      <Head>
        <title>
          Get great rewards from every purchase made at Bulk Nutrients
        </title>
        <meta
          name="title"
          content="Get great rewards from every purchase made at Bulk Nutrients"
        />
        <meta
          name="description"
          content="At Bulk Nutrients we reward you for being part of our community. Collect points and redeem for coupons. Climb the Bulk VIP ladder for more perks."
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/rewards`}
        />
      </Head>
      <Container
        as="main"
        id="main-content"
        className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
      >
        <section className="full-bleed relative h-[40vh] lg:h-screen lg:overflow-hidden">
          <Image
            src={hero}
            alt="man in gym clothes with Bulk Nutrients BCAA recovery"
            fill
            placeholder="blur"
            objectFit="cover"
          />
          <h1
            className="absolute bottom-auto left-0 right-0 top-4 
            m-auto h-fit w-fit bg-black px-4 py-1
            text-center text-[2rem] font-bold uppercase italic leading-8 text-white lg:bottom-auto
            lg:top-12.5 lg:text-[3rem] lg:leading-12 rounded-3xl"
          >
            Rewards Program
          </h1>
          <h2
            className="absolute bottom-[-18px] left-0 right-0 m-0 mx-auto h-fit w-fit bg-orange py-1 px-4
           text-center text-[1rem] font-bold leading-[1.2rem] text-white sm:text-[1.3rem] lg:bottom-auto lg:top-[120px] lg:p-2 lg:text-[1.8rem] rounded-3xl"
          >
            Get rewards every time you shop with Bulk Nutrients
          </h2>
        </section>

        <section className="pt-12.5 lg:pt-10">
          {customer ? (
            <StandoutParagraph>
              Check out your{' '}
              <TextLink href="/account/rewards">earned rewards</TextLink>!
            </StandoutParagraph>
          ) : (
            <StandoutParagraph>
              <TextLink href="/login">Login</TextLink> or{' '}
              <TextLink href="/create-account">sign up</TextLink> to check out
              your points!
            </StandoutParagraph>
          )}
          <StandoutParagraph>
            Every time you shop at Bulk Nutrients, you'll earn Whey Cool Points,
            which can be redeemed for awesome coupons and perks.
          </StandoutParagraph>
          <StandoutParagraph>
            To start earning Whey Cool Points, you'll need to create an account
            with us. So, if you've been checking out as a guest, now's the
            perfect time to sign up! Don't worry, it's free!
          </StandoutParagraph>
          <StandoutParagraph>
            You can accumulate Whey Cool points in a variety of ways, but the
            easiest is by simply shopping with us! Stock up on Australia's
            purest sports supplements and earn three Whey Cool Points per dollar
            spent. You can also collect points by...
          </StandoutParagraph>
        </section>

        <section>
          <RewardsWidget />
        </section>

        <section className="full-bleed relative mt-[250px] bg-orange px-0 pb-[120px] pt-[200px] text-black sm:mt-[280px] sm:pt-[250px]">
          <div className="absolute left-0 right-0 top-[-175px] mx-auto my-0 h-[350px] w-full overflow-hidden py-0 sm:top-[-200px] sm:h-[400px] sm:w-[400px]">
            <Image
              src={spending}
              alt="woman at gym holding Bulk Nutrients WPI and other products"
              fill
              placeholder="blur"
              className="rounded-2xl"
              objectFit="cover"
            />
          </div>

          <div className="max-w-narrow-page justify-self-center px-4">
            <h2 className="pb-2 text-left text-[1.5rem] font-bold capitalize italic text-white sm:text-[3rem] sm:leading-[4rem]">
              Spending Whey Cool Points
            </h2>

            <StandoutParagraph className="py-0 text-white">
              This is the fun part, spending all those hard-earned points on
              your next stash of supps! When you reach point milestones you can
              cash in your points for coupons to use on your next order.{' '}
              {customer ? (
                <span>
                  Check out your points balance{' '}
                  <TextLink href="/account/rewards" className="text-white!">
                    here
                  </TextLink>
                  !
                </span>
              ) : (
                <span>
                  Don't forget to{' '}
                  <TextLink href="/login" className="text-white!">
                    sign in
                  </TextLink>{' '}
                  and check your points balance!
                </span>
              )}
            </StandoutParagraph>
          </div>
          <VoucherWidget />
        </section>

        <section className="bg-white pt-[230px] dark:bg-black dark:text-white sm:pt-[100px]">
          <h2 className="pb-2 text-left text-[1.5rem] font-bold capitalize italic text-black dark:text-white sm:text-[3rem] sm:leading-[4rem]">
            Tier Benefits
          </h2>
          <StandoutParagraph>
            The more you spend on your favourite Aussie manufactured supps, the
            higher you climb on the Bulk Nutrients VIP ladder (and the greater
            and more exclusive benefits you receive!).
          </StandoutParagraph>
          <TiersWidget />
          <StandoutParagraph>
            Your tier is calculated every 12 months from reaching a tier. If you
            haven't spent enough to requalify for your existing tier, you'll
            move to a lower tier.
          </StandoutParagraph>
        </section>

        <section className="full-bleed bg-black px-0 py-[25px] text-white">
          <div className="mx-auto max-w-narrow-page px-4">
            <h2 className="pb-2 text-left text-[1.5rem] font-bold capitalize italic text-white sm:text-[3rem] sm:leading-[4rem]">
              FAQs
            </h2>
            <Accordion
              defaultOpen={false}
              items={FAQS.map((faq, index) => ({
                ...faq,
                className: '!text-white',
              }))}
            />
          </div>
        </section>
      </Container>
    </>
  );
}

Rewards.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
